import {LoginResponse} from "./model/LoginResponse";
import {DriverProfile} from "./model/DriverProfile";
import {UserDetails} from "./model/UserDetails";
import {create} from "zustand";
import {UserType} from "./enum/UserType";
import {ApiService} from "./ApiService";
import {StopDetails} from './model/StopDetails';
import {LoadDetails} from "./model/LoadDetails";
import {LoadInfo} from "./model/LoadInfo";
import { AppMap } from "./model/AppMap";
import freshMap from "../components/FreshMap";
import { PushNotificationsService } from "./PushNotificationsService";

export class AuthService {
    public static isLoggedIn(): boolean {
        return !!localStorage.getItem('token') && !!localStorage.getItem('username')?.toUpperCase();
    }

    public static processLogin(data: LoginResponse, credentials: { username: string, password: string }, loginData: { agreedToTerms: boolean, saveCredentials: boolean }): void {
        localStorage.setItem('token', data.loginToken);
        localStorage.setItem('type', data.type);
        localStorage.setItem('username', credentials.username);
        if (loginData.saveCredentials) {
            localStorage.setItem('save', 'true');
            localStorage.setItem('password', credentials.password);
        } else {
            localStorage.removeItem('save');
            localStorage.removeItem('password');
        }
        userStore.getState().clearUserData();
        userStore.getState().performLogin(credentials.username);
        window.history.pushState('', '', '/');
    }

    public static processLogout(): void {
        if (localStorage.getItem('save') === 'true') {
            // If remember me was checked then we keep only the credentials from local storage
            // We remove everything else that is in there
            const username = localStorage.getItem('username');
            const password = localStorage.getItem('password');
            localStorage.clear();
            localStorage.setItem('username', username || '');
            localStorage.setItem('password', password || '');
            localStorage.setItem('save', 'true');
        } else {
            localStorage.clear();
        }
        userStore.getState().clearUserData();
        window.history.pushState('', '', '/');
    }
}

export interface UserState {
    initialized: boolean;
    isLoggedIn: boolean;
    performedLogin: boolean;
    driver?: DriverProfile;
    user?: UserDetails;
    dmUserCode?: string;
    loadNumber?: string;
    loadInfo?: LoadInfo;
    stopDetails?: StopDetails[]
    profilePictureUrl?: string;
    username: string;
    type: UserType;
    leasePurchase?: number;
    appMap?: AppMap;
    claimRedirectPending: boolean;
    fetchUserData: () => Promise<void>;
    clearUserData: () => void;
    performLogin: (username: string) => void;
    reloadDriverProfile: () => Promise<void>;
    resetAppMap: () => Promise<void>;
}


export const userStore = create<UserState>((set, get) => ({
    initialized: false,
    isLoggedIn: AuthService.isLoggedIn(),
    performedLogin: false,
    driver: undefined,
    user: undefined,
    loadNumber: undefined,
    loadInfo: undefined,
    stopDetails: undefined,
    profilePictureUrl: '',
    leasePurchase: undefined,
    appMap: undefined,
    claimRedirectPending: false,
    username: localStorage.getItem('username') || '',
    type: localStorage.getItem('type') as UserType,
    fetchUserData: async () => {
        if (!AuthService.isLoggedIn()) {
            set({initialized: true})
            return;
        }

        const type = localStorage.getItem('type') as UserType;

        const driver =  await ApiService.getDriverProfile('login');
        const promiseResult = await Promise.all([
            ApiService.getUser("login"),
            driver?.unit ? (await ApiService.getLoadDetails('login', {unit: driver.unit as string}).catch(() => {return undefined})) : Promise.resolve(undefined)
        ])
        const user = promiseResult[0] as UserDetails;
        const load = (promiseResult[1] as LoadDetails)?.load;
        const stops = (promiseResult[1]?.stops as unknown as StopDetails[]);
        const loadNumber = load?.loadNumber;



            // Check for first sign in walkthrough purposes 
            const appMapResults : any = await Promise.all([
                driver?.unit ? (await ApiService.getDriverMap("login", {} , ).catch(e => {
                    if (e.errcode === "NOTFOUND") {
                        ApiService.setDriverMap( "login" , {appMap: JSON.stringify(freshMap) } )
    
                    }
                    return undefined
                })) : Promise.resolve(undefined)
            ])
    
            // Parse the object if it returns as not undefined, NEEDED 
            const pageMap: AppMap | undefined = appMapResults[0]?.appMap === undefined ? undefined : JSON.parse(appMapResults[0]?.appMap) as AppMap
            console.log(pageMap)

        localStorage.setItem('userId', user.id);
        localStorage.setItem('driverCode', driver.driverCode);
        localStorage.setItem('dmCode', driver.dmCode);

        set({
            driver,
            user,
            loadNumber: loadNumber,
            loadInfo: load,
            stopDetails: stops,
            dmUserCode: user.dmcode,
            isLoggedIn: AuthService.isLoggedIn(),
            initialized: true,
            username: localStorage.getItem('username') || '',
            type,
            leasePurchase: driver.leasePurchase,
            appMap: pageMap === undefined ? freshMap : pageMap,
            claimRedirectPending: true,
        });
        console.log("promiseResult", stops, load);

        const pushEligible = get().performedLogin && !!user?.id && type !== UserType.ADMIN && type !== UserType.INSPECTOR && type !== UserType.YARDDOG && type !== UserType.GUARDDOG && type !== UserType.DRIVER_MANAGER;
        if (pushEligible) {
            PushNotificationsService.registerAfterAppLogin(user.id).then(() => {
                set({performedLogin: false});
            });
        }
    },
    clearUserData: () => {
        set({isLoggedIn: false, driver: undefined, user: undefined, loadNumber: undefined, loadInfo: undefined, profilePictureUrl: '', username: '', type: undefined, leasePurchase: undefined, appMap: undefined, claimRedirectPending: false});
    },
    performLogin: (username: string) => {
        set({performedLogin: true, initialized: false, username, claimRedirectPending: false});
    },
    reloadDriverProfile: async () => {
        const driver = await ApiService.getDriverProfile("login");
        set({driver});
    },
    resetAppMap: async () => {
        await ApiService.setDriverMap("settings", {appMap: JSON.stringify(freshMap)});
        set({appMap: {...freshMap}});
    },
}));

export interface UserPushNotificationState {
    received: boolean,
    title: string,
    body: string,
    responseId: number,
    id: string,
    data: {}
}

export const UserPushNotificationStore = create<UserPushNotificationState>((set, get) => ({
    received: false,
    title: "",
    responseId: 0,
    body: "",
    id: "",
    data: {}
}))
